'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useRef } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import type { HeaderNavigationItem } from '@/ui/components/Header/header-types';
import {
  TRANSITION_DURATION_BASE,
  TRANSITION_DURATION_SHORT,
} from '@/core/constants/tokens';
import { trackInteraction } from '@/core/services/ga4/ga4-service';
import Button from '@/ui/components/Button/Button';
import MobileMenuItems from '@/ui/components/MobileMenuItems/MobileMenuItems';
import VectorGraphic from '@/ui/components/VectorGraphic/VectorGraphic';
import { MobileMenuProvider, useMobileMenu } from '@/ui/context/mobile-menu';
import useOnClickOutside from '@/ui/hooks/use-click-outside';

import $ from './MobileMenu.module.scss';

type MobileMenuContentProps = {
  navigationItems: HeaderNavigationItem[];
  menuOpenLabel?: string;
  menuCloseLabel?: string;
};

const MobileMenuContent = ({
  navigationItems,
  menuOpenLabel,
  menuCloseLabel,
}: MobileMenuContentProps) => {
  const { isOpen, toggleMenu, closeMenu } = useMobileMenu();
  const modalRef = useRef<HTMLDivElement>(null);
  const durationBase = TRANSITION_DURATION_BASE / 1000;
  const durationShort = TRANSITION_DURATION_SHORT / 1000;

  useOnClickOutside(modalRef, closeMenu);

  const handleToggleMenu = () => {
    trackInteraction({
      interaction_type: 'click',
      click_target: 'Mobile menu toggle',
      click_text: isOpen ? 'Menu close' : 'Menu open',
    });

    toggleMenu();
  };

  const handleCloseMenu = () => {
    trackInteraction({
      interaction_type: 'click',
      click_target: 'Mobile menu close',
      click_text: 'Menu close',
    });

    closeMenu();
  };

  return (
    <div className={$.container}>
      <Button
        variant="tertiary"
        className={$.button}
        onClick={handleToggleMenu}
        aria-expanded={isOpen}
        aria-controls="mobile-menu"
        ariaLabel={isOpen ? menuCloseLabel : menuOpenLabel}
      >
        <VectorGraphic
          name={isOpen ? 'icon-close' : 'icon-menu'}
          className={$.icon}
        />
      </Button>

      <AnimatePresence>
        {isOpen && (
          <RemoveScroll>
            <div className={$.wrapper}>
              <motion.div
                className={$.backdrop}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{
                  opacity: 0,
                  transition: {
                    duration: durationBase,
                    ease: 'easeInOut',
                  },
                }}
                transition={{
                  duration: durationBase,
                  ease: 'easeInOut',
                }}
                onClick={handleCloseMenu}
              >
                <motion.div
                  className={$.panel}
                  ref={modalRef}
                  initial={{ x: '-100%', opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{
                    x: '-100%',
                    opacity: 0,
                    transition: {
                      x: {
                        duration: durationShort,
                        ease: 'easeInOut',
                      },
                      opacity: {
                        duration: durationBase,
                        ease: 'easeInOut',
                      },
                    },
                  }}
                  transition={{
                    x: {
                      duration: durationBase,
                      ease: 'easeInOut',
                    },
                    opacity: {
                      duration: durationShort,
                      ease: 'easeInOut',
                    },
                  }}
                  onClick={e => e.stopPropagation()}
                >
                  <MobileMenuItems navigationItems={navigationItems} />
                </motion.div>
              </motion.div>
            </div>
          </RemoveScroll>
        )}
      </AnimatePresence>
    </div>
  );
};

export type MobileMenuProps = {
  navigationItems?: HeaderNavigationItem[];
  menuOpenLabel?: string;
  menuCloseLabel?: string;
};

const MobileMenu = ({
  navigationItems = [],
  menuOpenLabel,
  menuCloseLabel,
}: MobileMenuProps) => (
  <MobileMenuProvider>
    <MobileMenuContent
      navigationItems={navigationItems}
      menuOpenLabel={menuOpenLabel}
      menuCloseLabel={menuCloseLabel}
    />
  </MobileMenuProvider>
);

export default MobileMenu;
